import { useEffect, useRef, useState } from "react";
import { Spinner } from "@opero/ui";
import { getErrorMessage } from "@digiits-agency/polaris-sdk";
import { polaris } from "../lib/polaris-client";

const RESEND_COOLDOWN_SECONDS = 30;

interface ResendVerificationProps {
  email?: string;
}

type ResendStatus = "idle" | "sending" | "sent" | "error";

export function ResendVerification({ email }: ResendVerificationProps) {
  const [status, setStatus] = useState<ResendStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (cooldown <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setCooldown((c) => Math.max(0, c - 1));
      }, 1000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [cooldown]);

  const handleResend = async () => {
    if (status === "sending" || cooldown > 0) return;
    setStatus("sending");
    setError(null);
    try {
      await polaris.auth.sendVerificationEmail();
      setStatus("sent");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't resend — please try again in a minute."));
      setStatus("error");
    }
  };

  if (status === "sending") {
    return (
      <div className="flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Spinner size="sm" />
        <span>Resending…</span>
      </div>
    );
  }

  if (status === "sent") {
    return (
      <div className="text-sm text-slate-500 dark:text-slate-400">
        <p>
          {email ? (
            <>
              Verification email sent to <strong>{email}</strong>. Check your
              inbox.
            </>
          ) : (
            <>Verification email sent. Check your inbox.</>
          )}
        </p>
        {cooldown > 0 && (
          <p className="mt-1 text-xs text-slate-400">
            You can resend again in {cooldown}s.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="text-sm text-slate-500 dark:text-slate-400">
      <span>Didn&apos;t get the email? </span>
      <button
        type="button"
        onClick={handleResend}
        className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
      >
        Resend
      </button>
      {status === "error" && error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
